import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SiteSetting {
  id?: number;
  key: string;
  value: string;
  full_url?: string;
  type: 'text' | 'textarea' | 'image' | 'video' | 'url' | 'number';
  section?: string;
  label?: string;
  description?: string;
  order?: number;
}

export interface SettingsResponse {
  status: boolean;
  message?: string;
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class SiteSettingsService {
  private apiUrl = `${environment.apiUrl}/public/site-settings`;
  private adminApiUrl = `${environment.apiUrl}/admin/site-settings`;

  // Cached settings loaded at app startup
  private settingsCache = signal<any>({});

  constructor(private http: HttpClient) {}

  /**
   * Load settings from the static JSON file.
   * Called once via APP_INITIALIZER before any component renders.
   */
  loadFromJson(): Promise<void> {
    return fetch(`/assets/settings.json?v=${Date.now()}`)
      .then(res => res.json())
      .then(data => this.settingsCache.set(data))
      .catch(() => this.settingsCache.set({}));
  }

  /** Get the entire settings object */
  getAll(): any {
    return this.settingsCache();
  }

  /** Get a full section object (e.g. 'home_hero', 'contact') */
  getSection(section: string): any {
    return this.settingsCache()?.[section] ?? {};
  }

  /** Get a single setting entry { value, full_url, ... } */
  getSetting(section: string, key: string): any {
    return this.settingsCache()?.[section]?.[key];
  }

  /** Shorthand: get the `value` field of a setting, with a fallback */
  val(section: string, key: string, fallback = ''): string {
    return this.getSetting(section, key)?.value || fallback;
  }

  /** Shorthand: get the `full_url` field (for images/videos) */
  url(section: string, key: string, fallback = ''): string {
    return this.getSetting(section, key)?.full_url || fallback;
  }

  // ─── Admin APIs (unchanged) ───

  getAllSettings(): Observable<SettingsResponse> {
    return this.http.get<SettingsResponse>(this.apiUrl).pipe(
      tap(response => {
        if (response.status) {
          this.settingsCache.set(response.data);
        }
      })
    );
  }

  getSettingsBySection(section: string): Observable<SettingsResponse> {
    return this.http.get<SettingsResponse>(`${this.apiUrl}/${section}`);
  }

  getAdminSettings(): Observable<SettingsResponse> {
    return this.http.get<SettingsResponse>(this.adminApiUrl);
  }

  updateSetting(id: number, value: string): Observable<SettingsResponse> {
    return this.http.post<SettingsResponse>(`${this.adminApiUrl}/${id}`, { value });
  }

  updateSettingWithFile(id: number, file: File): Observable<SettingsResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<SettingsResponse>(`${this.adminApiUrl}/${id}`, formData);
  }

  bulkUpdate(settings: { [key: string]: string }): Observable<SettingsResponse> {
    return this.http.post<SettingsResponse>(`${this.adminApiUrl}/bulk-update`, { settings });
  }

  publishSettings(): Observable<SettingsResponse> {
    return this.http.post<SettingsResponse>(`${this.adminApiUrl}/publish`, {});
  }

  getSettingValue(section: string, key: string): string {
    return this.val(section, key);
  }

  getSettingUrl(section: string, key: string): string {
    return this.url(section, key);
  }
}
